import { ChangeDetectionStrategy, Component, signal } from '@angular/core';

import type { ICellRendererAngularComp } from 'ag-grid-angular';
import type { ICellRendererParams } from 'ag-grid-community';
import { getKeyValue, isGeoPoint } from 'na-lib';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [],
    template: `
    @if (text()) {
      <a
        [href]="link()"
        target="_blank"
        rel="noopener noreferrer"
        style="color: var(--warmOrange); background: rgba(255, 94, 56, 0.12); padding: 2px 6px; border-radius: 4px; text-decoration: underline;"
        >
        {{ text() }}
      </a>
    } @else {
    }
    `
})
export class ReportsListItemGeopointDisplay implements ICellRendererAngularComp {
  text = signal<string>('');
  link = signal<string>('');

  agInit(params: ICellRendererParams): void {
    this.refresh(params);
  }

  refresh(params: ICellRendererParams): boolean {
    const raw = this.getRawValue(params);
    const normalized = this.normalize(raw);

    if (!normalized) {
      this.text.set('');
      this.link.set('');
      return true;
    }

    const { lat, lng } = normalized;
    const txt = `${lat}, ${lng}`;
    this.text.set(txt);
    this.link.set(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lng}`)}`);
    return true;
  }

  private getRawValue(params: ICellRendererParams): any {
    const field = params.colDef.field;
    if (!field) return params.value;
    return getKeyValue(params.data, field) ?? params.value;
  }

  private normalize(v: any): { lat: number; lng: number } | null {
    if (!v) return null;

    if (typeof v === 'string') {
      const m = v.match(/^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/);
      if (!m) return null;
      const lat = Number(m[1]);
      const lng = Number(m[2]);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      return { lat, lng };
    }

    if (typeof v === 'object' && isGeoPoint(v)) {
      const gp = v;
      const lat = (gp.lat ?? gp.latitude ?? gp._latitude);
      const lng = (gp.lng ?? gp.longitude ?? gp._longitude);
      if (lat == null || lng == null) return null;
      const latN = Number(lat);
      const lngN = Number(lng);
      if (!Number.isFinite(latN) || !Number.isFinite(lngN)) return null;
      return { lat: latN, lng: lngN };
    }

    return null;
  }
}
